import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Settings2, ArrowLeft } from "lucide-react";
import { DataSelection, EventMapping, DataSettings, DataQualityTab } from "@/components/ga/ConfigTabs";
import { presetRange } from "@/components/ga/RangeBar";

const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Request failed");

export default function GaConfigurePage() {
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState(null);
  const load = useCallback(() => api.get("/ga4/config").then((r) => { setCfg(r.data); setSaved(JSON.stringify(r.data)); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!saved) return;
    const c = JSON.parse(saved);
    const rg = presetRange(c.settings.default_range);
    api.get("/ga4/report", { params: { start: rg.start, end: rg.end, compare: "none" } }).then((r) => setReport(r.data.report)).catch(() => {});
  }, [saved]);
  const dirty = cfg && saved !== JSON.stringify(cfg);
  const save = async () => {
    setSaving(true);
    try {
      const event_map = Object.fromEntries(Object.entries(cfg.event_map).map(([k, v]) => [k, { confirmed: v.confirmed, rules: v.rules.filter((r) => r.event) }]));
      await api.put("/ga4/config", { metrics: cfg.metrics, dimensions: cfg.dimensions, event_map, settings: cfg.settings });
      toast.success("Analytics configuration saved"); setReport(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const syncNow = async () => { setSyncing(true); try { await api.post("/ga4/sync-now"); toast.success("Synced"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); } };
  const backfill = async (months) => { try { await api.post("/ga4/backfill", { months }); toast.success("Backfill started"); setTimeout(load, 8000); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!cfg) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-6xl" data-testid="ga-configure-page">
      <Link to="/marketing/website-analytics" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4" />Website Analytics</Link>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Settings2 className="w-6 h-6 text-indigo-600" />Configure Analytics</h1>
          <p className="text-sm text-slate-500">Settings for this company only.</p>
        </div>
        <Button onClick={save} disabled={!dirty || saving || !cfg.can_edit} data-testid="ga-config-save-btn">{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}</Button>
      </div>
      <Tabs defaultValue="selection">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="selection" data-testid="ga-tab-selection">Data selection</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="ga-tab-mapping">Event mapping</TabsTrigger>
          <TabsTrigger value="settings" data-testid="ga-tab-settings">Data settings</TabsTrigger>
          <TabsTrigger value="quality" data-testid="ga-tab-quality">Data quality</TabsTrigger>
        </TabsList>
        <TabsContent value="selection" className="mt-4"><DataSelection cfg={cfg} set={setCfg} /></TabsContent>
        <TabsContent value="mapping" className="mt-4"><EventMapping cfg={cfg} set={setCfg} /></TabsContent>
        <TabsContent value="settings" className="mt-4"><DataSettings cfg={cfg} set={setCfg} onSyncNow={syncNow} onBackfill={backfill} syncing={syncing} /></TabsContent>
        <TabsContent value="quality" className="mt-4"><DataQualityTab report={report} /></TabsContent>
      </Tabs>
    </div>
  );
}
